import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Heading,
  Button,
  HStack,
  VStack,
  Text,
  Avatar,
  Spinner,
  useDisclosure,
  IconButton,
} from '@chakra-ui/react';
import { tasksAPI } from '../services/api';
import { Task, Project, User } from '../types';
import TaskModal from './TaskModal';
import ProjectStageDialogs from './ProjectStageDialogs';
import { toast } from '../utils/toast';
import { sortTasksByPriority } from '../utils/taskSort';
import { resolveLogoUrl } from '../utils/logoUrl';
import { getTaskLastCommentPreview } from '../utils/taskCommentPreview';
import TaskDependencyDisplay from './TaskDependencyDisplay';
import { useAuth } from '../context/AuthContext';
import { useProjectStages } from '../hooks/useProjectStages';
import { useListViewStageCollapse } from '../hooks/useListViewStageCollapse';
import { useProjectActivityState } from '../context/ProjectActivityContext';
import { ProjectActivityBanner, ActivityDot, ActivityIndicatorBadge } from './ActivityIndicatorBadge';
import { formatRelativeActivityTime } from '../utils/activityIndicators';

const PRIORITY_PILLS: Record<Task['priority'], { bg: string; label: string }> = {
  high: { bg: '#ef4444', label: 'High' },
  medium: { bg: '#f97316', label: 'Medium' },
  low: { bg: '#6b7280', label: 'Low' },
};

const StarIcon = ({ filled = false, size = 16 }: { filled?: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ChevronIcon = ({ expanded, size = 16 }: { expanded: boolean; size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s ease' }}
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

interface TaskListViewProps {
  projectId: string;
  project: Project;
  onProjectUpdate?: () => void;
  initialTaskId?: string;
  focusCommentId?: string;
  onFocusConsumed?: () => void;
}

const LIST_GRID_COLUMNS =
  'minmax(220px, 2.4fr) minmax(160px, 1.6fr) minmax(110px, 1fr) minmax(110px, 0.95fr) minmax(100px, 0.85fr) minmax(110px, 0.95fr)';

const LIST_TABLE_MIN_W = '1040px';

const LIST_GRID = {
  display: 'grid',
  gridTemplateColumns: LIST_GRID_COLUMNS,
  alignItems: 'center',
  width: '100%',
  minW: LIST_TABLE_MIN_W,
} as const;

const GRID_CELL = {
  px: 4,
  py: 3,
  borderRightWidth: 1,
  borderRightColor: 'gray.100',
  _last: { borderRightWidth: 0 },
} as const;

const HEADER_CELL = {
  ...GRID_CELL,
  fontSize: 'sm',
  fontWeight: 'normal' as const,
  color: 'gray.500',
  py: 2.5,
  bg: 'white',
};

type PriorityPillProps = { priority: Task['priority'] };

function PriorityPill({ priority }: PriorityPillProps) {
  const meta = PRIORITY_PILLS[priority] ?? PRIORITY_PILLS.medium;
  return (
    <Box
      as="span"
      display="inline-block"
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="medium"
      whiteSpace="nowrap"
      color="white"
      style={{ backgroundColor: meta.bg }}
    >
      {meta.label}
    </Box>
  );
}

function TaskRowCheckbox() {
  return (
    <Box
      flexShrink={0}
      w="18px"
      h="18px"
      borderRadius="full"
      borderWidth="1.5px"
      borderColor="gray.300"
      bg="white"
    />
  );
}

function ListColumnHeaders() {
  return (
    <Box
      {...LIST_GRID}
      borderBottomWidth={1}
      borderColor="gray.200"
      display={{ base: 'none', lg: 'grid' }}
    >
      <Box {...HEADER_CELL}>Task name</Box>
      <Box {...HEADER_CELL}>Dependency</Box>
      <Box {...HEADER_CELL}>Assignee</Box>
      <Box {...HEADER_CELL}>Due date</Box>
      <Box {...HEADER_CELL}>Priority</Box>
      <Box {...HEADER_CELL} borderRightWidth={0}>Progress</Box>
    </Box>
  );
}

function ListDataCell({ children }: { children: React.ReactNode }) {
  return (
    <Box {...GRID_CELL} fontSize="sm" minW={0}>
      {children}
    </Box>
  );
}

function TaskNameCell({ task }: { task: Task }) {
  const lastComment = getTaskLastCommentPreview(task);

  return (
    <HStack gap={3} align="flex-start" minW={0}>
      <Box pt={0.5}>
        <TaskRowCheckbox />
      </Box>
      <Box minW={0} flex={1}>
        <Text color="gray.900" lineHeight="short">
          {task.title}
        </Text>
        {lastComment && (
          <Text mt={1} fontSize="xs" color="gray.500" lineClamp={2}>
            {lastComment.text}
          </Text>
        )}
      </Box>
    </HStack>
  );
}

function formatDueDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function getAssigneeLabel(task: Task): string {
  const names: string[] = [];
  if (Array.isArray(task.participants)) {
    task.participants.forEach((p) => {
      if (p?.name) names.push(p.name);
    });
  }
  if (task.assignedTo?.name && !names.includes(task.assignedTo.name)) {
    names.unshift(task.assignedTo.name);
  }
  return names.length > 0 ? names.join(', ') : '—';
}

function getUserAvatarSrc(u: User): string | null {
  const direct = (u as any)?.avatarUrl;
  if (direct) return resolveLogoUrl(direct);
  const stored = (u as any)?.avatar?.storedFilename;
  if (stored) return resolveLogoUrl(`/api/user-avatars/${encodeURIComponent(String(stored))}`);
  return null;
}

function getDisplayAssignees(task: Task): User[] {
  const list: User[] = [];
  if (task.assignedTo) list.push(task.assignedTo);
  if (Array.isArray(task.participants)) {
    task.participants.forEach((p) => {
      const id = (p as any)?._id || (p as any)?.id;
      if (!list.some((x) => ((x as any)?._id || (x as any)?.id) === id)) {
        list.push(p);
      }
    });
  }
  return list.slice(0, 3);
}

function getUserInitials(u: User): string {
  return (u.name?.split(' ').map((n) => n[0]).join('').toUpperCase() || '?').slice(0, 2);
}

function AssigneeAvatar({ user }: { user: User }) {
  const [imgError, setImgError] = useState(false);
  const src = getUserAvatarSrc(user);
  if (src && !imgError) {
    return (
      <img
        src={src}
        alt={user.name || 'User'}
        onError={() => setImgError(true)}
        style={{
          width: '28px',
          height: '28px',
          borderRadius: '9999px',
          objectFit: 'cover',
          border: '1px solid #e5e7eb',
        }}
      />
    );
  }
  return (
    <Avatar.Root size="xs">
      <Avatar.Fallback bg="gray.100" color="gray.600" fontSize="2xs">
        {getUserInitials(user)}
      </Avatar.Fallback>
    </Avatar.Root>
  );
}

interface TaskListRowProps {
  task: Task;
  isLast: boolean;
  rowIndex: number;
  isUpdated?: boolean;
  onClick: () => void;
}

const TaskListRowDesktop: React.FC<TaskListRowProps> = ({ task, isLast, isUpdated = false, onClick }) => {
  const assignees = getDisplayAssignees(task);
  const pct = task.completionPercent ?? 0;

  return (
    <Box
      display={{ base: 'none', lg: 'block' }}
      borderBottomWidth={isLast ? 0 : 1}
      borderColor="gray.100"
      bg={isUpdated ? 'blue.50' : 'white'}
      borderLeftWidth={isUpdated ? 3 : 0}
      borderLeftColor={isUpdated ? 'blue.400' : 'transparent'}
      _hover={{ bg: isUpdated ? 'blue.100' : 'gray.50', cursor: 'pointer' }}
      onClick={onClick}
    >
      <Box {...LIST_GRID}>
        <ListDataCell>
          <HStack gap={2} minW={0}>
            {isUpdated && <ActivityDot title="Updated since your last visit" />}
            <TaskNameCell task={task} />
            {isUpdated && (
              <ActivityIndicatorBadge
                label={formatRelativeActivityTime(task.updatedAt) || 'Updated'}
              />
            )}
          </HStack>
        </ListDataCell>
        <ListDataCell>
          <TaskDependencyDisplay task={task} showLabel={false} emptyPlaceholder />
        </ListDataCell>
        <ListDataCell>
          <HStack gap={1.5} title={getAssigneeLabel(task)} flexWrap="wrap">
            {assignees.length > 0 ? (
              assignees.map((u) => {
                const key = (u as any)?._id || (u as any)?.id || u.name;
                return <AssigneeAvatar key={key} user={u} />;
              })
            ) : (
              <Text fontSize="sm" color="gray.400">—</Text>
            )}
          </HStack>
        </ListDataCell>
        <ListDataCell>
          <Text color="gray.500" whiteSpace="nowrap">{formatDueDate(task.dueDate)}</Text>
        </ListDataCell>
        <ListDataCell>
          <PriorityPill priority={task.priority} />
        </ListDataCell>
        <Box {...GRID_CELL} borderRightWidth={0}>
          <HStack gap={2} minW={0}>
            <Box flex={1} h="4px" borderRadius="full" bg="gray.100" overflow="hidden" minW="48px">
              <Box h="100%" w={`${pct}%`} bg="blue.400" borderRadius="full" />
            </Box>
            <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" minW="32px">
              {pct}%
            </Text>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

const TaskListRowMobile: React.FC<TaskListRowProps> = ({ task, isLast, isUpdated = false, onClick }) => {
  const assignees = getDisplayAssignees(task);
  const pct = task.completionPercent ?? 0;
  const lastComment = getTaskLastCommentPreview(task);
  const hasDeps = (task.dependsOn?.length ?? 0) > 0;
  const primaryAssignee = assignees[0];
  const hasDueDate = !!task.dueDate;

  return (
    <Box
      px={3}
      py={3}
      borderBottomWidth={isLast ? 0 : 1}
      borderColor="gray.100"
      bg={isUpdated ? 'blue.50' : 'white'}
      borderLeftWidth={isUpdated ? 3 : 0}
      borderLeftColor={isUpdated ? 'blue.400' : 'transparent'}
      _active={{ bg: isUpdated ? 'blue.100' : 'gray.50' }}
      onClick={onClick}
    >
      <HStack align="flex-start" gap={2.5} minW={0}>
        <Box pt={0.5} flexShrink={0}>
          <TaskRowCheckbox />
        </Box>
        <Box flex={1} minW={0}>
          <HStack justify="space-between" align="flex-start" gap={2} mb={2}>
            <HStack gap={1.5} flex={1} minW={0}>
              {isUpdated && <ActivityDot title="Updated since your last visit" />}
              <Text fontSize="sm" fontWeight="medium" color="gray.900" lineHeight="short" flex={1}>
                {task.title}
              </Text>
            </HStack>
            <HStack gap={1} flexShrink={0}>
              {isUpdated && (
                <ActivityIndicatorBadge
                  label={formatRelativeActivityTime(task.updatedAt) || 'Updated'}
                />
              )}
              <PriorityPill priority={task.priority} />
            </HStack>
          </HStack>

          {lastComment && (
            <Text fontSize="xs" color="gray.500" lineClamp={2} mb={2}>
              {lastComment.text}
            </Text>
          )}

          <HStack gap={3} flexWrap="wrap" alignItems="center">
            {primaryAssignee && (
              <HStack gap={1.5} title={getAssigneeLabel(task)}>
                <AssigneeAvatar user={primaryAssignee} />
                {assignees.length > 1 && (
                  <Text fontSize="xs" color="gray.400">+{assignees.length - 1}</Text>
                )}
              </HStack>
            )}
            {hasDueDate && (
              <Text fontSize="xs" color="gray.500">{formatDueDate(task.dueDate)}</Text>
            )}
            <HStack gap={1.5} flex={1} minW="72px" maxW="140px" ml={!primaryAssignee && !hasDueDate ? 0 : undefined}>
              <Box flex={1} h="4px" borderRadius="full" bg="gray.100" overflow="hidden">
                <Box h="100%" w={`${pct}%`} bg="blue.400" borderRadius="full" />
              </Box>
              <Text fontSize="xs" color="gray.500" flexShrink={0}>{pct}%</Text>
            </HStack>
          </HStack>

          {hasDeps && (
            <Box mt={2} fontSize="xs" color="gray.600">
              <TaskDependencyDisplay task={task} showLabel={false} />
            </Box>
          )}
        </Box>
      </HStack>
    </Box>
  );
};

interface StageSectionHeaderProps {
  column: { id: string; title: string };
  columnIndex: number;
  isFinished: boolean;
  canManageStages: boolean;
  columnsLength: number;
  taskCount: number;
  collapsed: boolean;
  onToggleCollapse: () => void;
  stages: ReturnType<typeof useProjectStages>;
  project: Project;
}

function StageSectionHeader({
  column,
  columnIndex,
  isFinished,
  canManageStages,
  columnsLength,
  taskCount,
  collapsed,
  onToggleCollapse,
  stages,
  project,
}: StageSectionHeaderProps) {
  return (
    <HStack
      px={{ base: 3, lg: 4 }}
      pt={columnIndex === 0 ? { base: 3, lg: 4 } : { base: 4, lg: 6 }}
      pb={2}
      justifyContent="space-between"
      alignItems="center"
      borderTopWidth={columnIndex === 0 ? 0 : 1}
      borderColor="gray.100"
      gap={2}
    >
      <HStack
        gap={2}
        minW={0}
        flex={1}
        cursor="pointer"
        onClick={onToggleCollapse}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleCollapse();
          }
        }}
        _hover={{ opacity: 0.85 }}
      >
        <Box color="gray.500" flexShrink={0} display="flex" aria-hidden>
          <ChevronIcon expanded={!collapsed} />
        </Box>
        <Text fontSize="sm" fontWeight="bold" color="gray.900">
          {column.title}
        </Text>
        {isFinished && (
          <Box as="span" display="inline-flex" color="yellow.500" title="Stage for finished tasks">
            <StarIcon filled size={14} />
          </Box>
        )}
        <Text fontSize="xs" color="gray.400" fontWeight="normal">
          {taskCount}
        </Text>
      </HStack>
      {canManageStages && (
        <HStack gap={0} flexShrink={0} display={{ base: 'none', sm: 'flex' }} onClick={(e) => e.stopPropagation()}>
          <IconButton
            aria-label="Move stage up"
            size="xs"
            variant="ghost"
            disabled={columnIndex === 0}
            onClick={() => stages.handleReorderStage(column.id, 'up')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </IconButton>
          <IconButton
            aria-label="Move stage down"
            size="xs"
            variant="ghost"
            disabled={columnIndex === columnsLength - 1}
            onClick={() => stages.handleReorderStage(column.id, 'down')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </IconButton>
          {!project.finishedColumnId && (
            <IconButton
              aria-label="Mark as finished stage"
              size="xs"
              variant="ghost"
              colorScheme="yellow"
              onClick={() => stages.handleSetAsFinished(column.id)}
              title="Mark as finished stage"
            >
              <StarIcon size={16} />
            </IconButton>
          )}
          <IconButton
            aria-label="Delete stage"
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={() => stages.handleDeleteStageClick(column.id)}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </IconButton>
        </HStack>
      )}
    </HStack>
  );
}

const TaskListView: React.FC<TaskListViewProps> = ({
  projectId,
  project,
  onProjectUpdate,
  initialTaskId,
  focusCommentId,
  onFocusConsumed,
}) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const { open, onOpen, onClose } = useDisclosure();
  const [activeFocusCommentId, setActiveFocusCommentId] = useState<string | undefined>(focusCommentId);
  const openedInitialTaskRef = React.useRef<string | null>(null);

  const stages = useProjectStages(projectId, project, user, onProjectUpdate);
  const { columns, canManageStages } = stages;
  const { isCollapsed, toggleCollapsed } = useListViewStageCollapse(projectId);

  const loadTasks = useCallback(async () => {
    try {
      const response = await tasksAPI.getByProject(projectId);
      setTasks(response.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load tasks', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!initialTaskId || loading || tasks.length === 0) return;
    if (openedInitialTaskRef.current === initialTaskId) return;
    const match = tasks.find((t) => t._id === initialTaskId);
    if (!match) return;
    openedInitialTaskRef.current = initialTaskId;
    setSelectedTask(match);
    setActiveFocusCommentId(focusCommentId);
    onOpen();
    onFocusConsumed?.();
  }, [initialTaskId, focusCommentId, loading, tasks, onOpen, onFocusConsumed]);

  const tasksByColumn = useMemo(() => {
    const map: Record<string, Task[]> = {};
    columns.forEach((col) => {
      map[col.id] = sortTasksByPriority(tasks.filter((t) => t.status === col.id));
    });
    return map;
  }, [tasks, columns]);

  const activity = useProjectActivityState(projectId, user?.id, tasks);

  const handleTaskClick = (task: Task) => {
    activity.markTaskSeen(task._id);
    setSelectedTask(task);
    onOpen();
  };

  const handleCreateTask = () => {
    setSelectedTask(null);
    onOpen();
  };

  const handleTaskSaved = () => {
    setSelectedTask(null);
    loadTasks();
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box width="100%" maxW="100%" overflow="hidden">
      <ProjectActivityBanner
        updatedCount={activity.updatedTaskCount}
        onMarkAllSeen={activity.markAllSeen}
      />
      <VStack align="stretch" gap={3} mb={{ base: 4, lg: 8 }} display={{ base: 'flex', lg: 'none' }}>
        <Heading size="lg" color="gray.800">
          List View
        </Heading>
        <HStack gap={2}>
          {canManageStages && (
            <Button variant="outline" onClick={stages.onStageDialogOpen} borderRadius="lg" flex={1} size="sm">
              + Add Stage
            </Button>
          )}
          <Button
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            color="white"
            onClick={handleCreateTask}
            borderRadius="lg"
            fontWeight="semibold"
            flex={1}
            size="sm"
          >
            + New Task
          </Button>
        </HStack>
      </VStack>

      <HStack justifyContent="space-between" mb={8} flexWrap="wrap" gap={3} display={{ base: 'none', lg: 'flex' }}>
        <Heading size="xl" color="gray.800">
          List View
        </Heading>
        <HStack gap={3}>
          {canManageStages && (
            <Button variant="outline" onClick={stages.onStageDialogOpen} borderRadius="lg">
              <HStack gap={2}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span>Add Stage</span>
              </HStack>
            </Button>
          )}
          <Button
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            color="white"
            onClick={handleCreateTask}
            borderRadius="lg"
            fontWeight="semibold"
          >
            + New Task
          </Button>
        </HStack>
      </HStack>

      <Box
        borderWidth={1}
        borderColor="gray.200"
        borderRadius="lg"
        overflow="hidden"
        bg="white"
        maxW="100%"
      >
        {/* Desktop table */}
        <Box display={{ base: 'none', lg: 'block' }} overflowX="auto">
          <Box minW={LIST_TABLE_MIN_W}>
            <ListColumnHeaders />
            {columns.map((column, columnIndex) => {
              const columnTasks = tasksByColumn[column.id] || [];
              const isFinished = project.finishedColumnId === column.id;
              const isLastSection = columnIndex === columns.length - 1;
              const collapsed = isCollapsed(column.id);

              return (
                <Box key={`desktop-${column.id}`}>
                  <StageSectionHeader
                    column={column}
                    columnIndex={columnIndex}
                    isFinished={isFinished}
                    canManageStages={canManageStages}
                    columnsLength={columns.length}
                    taskCount={columnTasks.length}
                    collapsed={collapsed}
                    onToggleCollapse={() => toggleCollapsed(column.id)}
                    stages={stages}
                    project={project}
                  />
                  {!collapsed && (
                    columnTasks.length === 0 ? (
                      <Text px={4} py={3} pb={isLastSection ? 4 : 3} fontSize="sm" color="gray.400" borderTopWidth={1} borderColor="gray.100">
                        No tasks in this stage
                      </Text>
                    ) : (
                      <Box borderTopWidth={1} borderColor="gray.100">
                        {columnTasks.map((task, index) => (
                          <TaskListRowDesktop
                            key={task._id}
                            task={task}
                            isLast={index === columnTasks.length - 1}
                            rowIndex={index}
                            isUpdated={activity.isTaskUpdated(task)}
                            onClick={() => handleTaskClick(task)}
                          />
                        ))}
                      </Box>
                    )
                  )}
                  {!collapsed && !isLastSection && columnTasks.length > 0 && (
                    <Box borderBottomWidth={1} borderColor="gray.200" />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Mobile list */}
        <Box display={{ base: 'block', lg: 'none' }}>
          {columns.map((column, columnIndex) => {
            const columnTasks = tasksByColumn[column.id] || [];
            const isFinished = project.finishedColumnId === column.id;
            const isLastSection = columnIndex === columns.length - 1;
            const collapsed = isCollapsed(column.id);

            return (
              <Box key={`mobile-${column.id}`}>
                <StageSectionHeader
                  column={column}
                  columnIndex={columnIndex}
                  isFinished={isFinished}
                  canManageStages={canManageStages}
                  columnsLength={columns.length}
                  taskCount={columnTasks.length}
                  collapsed={collapsed}
                  onToggleCollapse={() => toggleCollapsed(column.id)}
                  stages={stages}
                  project={project}
                />
                {!collapsed && (
                  columnTasks.length === 0 ? (
                    <Text px={3} py={3} pb={isLastSection ? 3 : 2} fontSize="sm" color="gray.400" borderTopWidth={1} borderColor="gray.100">
                      No tasks in this stage
                    </Text>
                  ) : (
                    <Box borderTopWidth={1} borderColor="gray.100">
                      {columnTasks.map((task, index) => (
                        <TaskListRowMobile
                          key={task._id}
                          task={task}
                          isLast={index === columnTasks.length - 1}
                          rowIndex={index}
                          isUpdated={activity.isTaskUpdated(task)}
                          onClick={() => handleTaskClick(task)}
                        />
                      ))}
                    </Box>
                  )
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <TaskModal
        open={open}
        onClose={() => {
          setActiveFocusCommentId(undefined);
          onClose();
        }}
        projectId={projectId}
        project={project}
        task={selectedTask}
        onSaved={handleTaskSaved}
        focusCommentId={activeFocusCommentId}
      />

      <ProjectStageDialogs
        project={project}
        stageDialogOpen={stages.stageDialogOpen}
        closeStageDialog={stages.closeStageDialog}
        deleteDialogOpen={stages.deleteDialogOpen}
        onDeleteDialogClose={stages.onDeleteDialogClose}
        newStageTitle={stages.newStageTitle}
        setNewStageTitle={stages.setNewStageTitle}
        newStageColor={stages.newStageColor}
        setNewStageColor={stages.setNewStageColor}
        newStageSetAsFinished={stages.newStageSetAsFinished}
        setNewStageSetAsFinished={stages.setNewStageSetAsFinished}
        handleAddStage={stages.handleAddStage}
        confirmDeleteStage={stages.confirmDeleteStage}
      />
    </Box>
  );
};

export default TaskListView;
